import { useEffect, useState } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useUserStore } from '@/stores/userStore';
import { useAuthStore } from '@/auth/authStore';
import { Avatar, RankMedal } from '@/art';
import { BackButton } from '@/components/BackButton';
import { UserQuickMenu } from '@/components/chat/UserQuickMenu';
import { useChatDock } from '@/stores/chatDockStore';
import { useT } from '@/i18n/useI18n';
import type { I18nKey } from '@/i18n/translations';
import {
  fetchLeaderboard,
  fetchLeaderboardByTier,
  formatTime,
  type LeaderboardMode,
  type LeaderboardRow,
  type LeaderboardTab,
} from '@/leaderboard/leaderboardApi';
import { RANK_TIER_ORDER } from '@shared/gameConfig';
import type { RankTier } from '@shared/types';

const TIER_LABEL: Record<RankTier, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
  diamond: 'Diamond',
  master: 'Master',
  challenger: 'Challenger',
};
const TIER_GLOW: Record<RankTier, string> = {
  bronze: 'rgba(205,127,50,0.4)',
  silver: 'rgba(220,220,220,0.4)',
  gold: 'rgba(244,199,36,0.5)',
  platinum: 'rgba(34,211,238,0.5)',
  diamond: 'rgba(59,130,246,0.55)',
  master: 'rgba(155,93,229,0.6)',
  challenger: 'rgba(244,199,36,0.7)',
};

const DIVISION_ROMAN = ['', 'I', 'II', 'III', 'IV'] as const;

export function LeaderboardScreen() {
  const go = useUIStore((s) => s.go);
  const t = useT();
  const profile = useUserStore((s) => s.profile);
  const me = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<LeaderboardTab>('mmr');
  const [mode, setMode] = useState<LeaderboardMode>('solo');
  const [rows, setRows] = useState<LeaderboardRow[] | null>(null);
  const [byTier, setByTier] = useState<Partial<Record<RankTier, LeaderboardRow[]>> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Player profile popup (same quick menu as chat) — opened by tapping a row.
  const [peekUserId, setPeekUserId] = useState<string | null>(null);
  const openDm = useChatDock((s) => s.openDm);

  useEffect(() => {
    setRows(null);
    setByTier(null);
    setErr(null);
    if (tab === 'tiers') {
      fetchLeaderboardByTier(mode)
        .then(setByTier)
        .catch((e) => setErr(String(e)));
    } else {
      fetchLeaderboard(tab, mode)
        .then(setRows)
        .catch((e) => setErr(String(e)));
    }
  }, [tab, mode]);

  const mmrOf = (r: { mmrSolo: number; mmrDuo: number }) =>
    mode === 'duo' ? r.mmrDuo : r.mmrSolo;

  const myRow = me ? rows?.find((r) => r.id === me.id) : null;
  const myRank = myRow ? rows!.indexOf(myRow) + 1 : null;

  return (
    <div className="screen">
      <div className="hex-bg" />

      <div style={{ padding: '14px 18px 0' }}>
        <BackButton onClick={() => go('home')} />
        <div className="display" style={{ fontSize: 26, marginTop: 10 }}>
          {t('leaderboard.title')}
        </div>
      </div>

      <div style={{ padding: '12px 18px' }}>
        <div
          style={{
            display: 'flex',
            borderRadius: 10,
            padding: 3,
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid var(--hairline)',
          }}
        >
          {(
            [
              { id: 'mmr', labelKey: 'leaderboard.tab_mmr' },
              { id: 'time', labelKey: 'leaderboard.tab_time' },
              { id: 'tiers', labelKey: 'leaderboard.tab_byrank' },
            ] as const satisfies ReadonlyArray<{ id: LeaderboardTab; labelKey: I18nKey }>
          ).map((k) => (
            <div
              key={k.id}
              onClick={() => setTab(k.id)}
              style={{
                flex: 1,
                padding: 8,
                textAlign: 'center',
                cursor: 'pointer',
                borderRadius: 8,
                fontFamily: 'var(--f-display)',
                fontSize: 11,
                letterSpacing: '0.08em',
                background: tab === k.id ? 'var(--c-yellow)' : 'transparent',
                color: tab === k.id ? '#1E1B4B' : 'var(--ink-muted)',
                transition: 'background 0.2s, color 0.2s',
              }}
            >
              {t(k.labelKey)}
            </div>
          ))}
        </div>

        {tab !== 'time' && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {(
              [
                { id: 'solo', labelKey: 'leaderboard.mode_solo' },
                { id: 'duo', labelKey: 'leaderboard.mode_duo' },
              ] as const satisfies ReadonlyArray<{ id: LeaderboardMode; labelKey: I18nKey }>
            ).map((m) => (
              <div
                key={m.id}
                onClick={() => setMode(m.id)}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: 8,
                  fontFamily: 'var(--f-display)',
                  fontSize: 10,
                  letterSpacing: '0.08em',
                  background: mode === m.id ? 'rgba(34,211,238,0.16)' : 'rgba(0,0,0,0.3)',
                  color: mode === m.id ? 'var(--c-cyan)' : 'var(--ink-muted)',
                  border: `1px solid ${mode === m.id ? 'var(--c-cyan)' : 'var(--hairline)'}`,
                  transition: 'background 0.2s, color 0.2s',
                }}
              >
                {t(m.labelKey)}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 18px 12px' }}>
        {err && (
          <div
            style={{
              padding: 12,
              borderRadius: 10,
              background: 'rgba(230,57,70,0.16)',
              border: '1px solid rgba(230,57,70,0.4)',
              color: 'var(--c-red)',
              fontSize: 12,
              marginBottom: 10,
            }}
          >
            {err}
          </div>
        )}

        {((tab !== 'tiers' && !rows) || (tab === 'tiers' && !byTier)) && !err && (
          <div
            style={{
              padding: 30,
              color: 'var(--ink-dim)',
              fontSize: 12,
              textAlign: 'center',
              fontFamily: 'var(--f-display)',
              letterSpacing: '0.15em',
            }}
          >
            {t('common.loading').toUpperCase()}
          </div>
        )}

        {tab !== 'tiers' && rows && rows.length === 0 && (
          <div
            style={{
              padding: 30,
              color: 'var(--ink-dim)',
              fontSize: 12,
              textAlign: 'center',
            }}
          >
            {tab === 'time'
              ? t('leaderboard.empty_time')
              : t('leaderboard.empty_players')}
          </div>
        )}

        {tab !== 'tiers' &&
          rows?.map((p, i) => {
            const isMe = me && p.id === me.id;
            const rankPlace = i + 1;
            return (
              <LeaderRow
                key={p.id}
                place={rankPlace}
                row={p}
                tab={tab}
                mode={mode}
                mmrValue={mmrOf(p)}
                isMe={Boolean(isMe)}
                onPeek={() => setPeekUserId(p.id)}
              />
            );
          })}

        {/* Per-tier top 10 view */}
        {tab === 'tiers' && byTier && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {[...RANK_TIER_ORDER].reverse().map((t) => {
              const list = byTier[t] ?? [];
              return (
                <TierSection
                  key={t}
                  tier={t}
                  rows={list}
                  mode={mode}
                  meId={me?.id}
                  onPeek={setPeekUserId}
                />
              );
            })}
          </div>
        )}

        {/* Sticky "you" footer if user not in the visible top */}
        {tab !== 'tiers' && me && rows && !myRow && (
          <div
            style={{
              marginTop: 14,
              padding: '10px 12px',
              background: 'rgba(34,211,238,0.08)',
              border: '1px solid var(--c-cyan)',
              borderRadius: 12,
              boxShadow: '0 0 14px rgba(34,211,238,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 28,
                height: 24,
                borderRadius: 6,
                background: 'rgba(0,0,0,0.4)',
                color: 'var(--c-cyan)',
                fontFamily: 'var(--f-display)',
                fontSize: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              —
            </div>
            <Avatar
              color={profile.avatarColor}
              size={32}
              ring={false}
              photoUrl={profile.avatarUrl}
              characterUrl={profile.characterIdleUrl}
              frame={profile.frame}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="display" style={{ fontSize: 13 }}>
                {profile.name}{' '}
                <span style={{ fontSize: 9, color: 'var(--c-cyan)' }}>{t('leaderboard.you')}</span>
              </div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--ink-muted)' }}>
                {tab === 'time' ? t('leaderboard.unranked_yet') : `${mmrOf(profile)} MMR`}
              </div>
            </div>
            <RankMedal rank={mode === 'duo' ? profile.rankDuo : profile.rank} size={28} />
          </div>
        )}

        {tab !== 'tiers' && myRow && myRank && myRank > 0 && (
          <div
            style={{
              marginTop: 10,
              fontSize: 10,
              color: 'var(--ink-dim)',
              textAlign: 'center',
              fontFamily: 'var(--f-mono)',
            }}
          >
            {t('leaderboard.your_position', { n: myRank })}
          </div>
        )}
      </div>

      {peekUserId && (
        <UserQuickMenu
          userId={peekUserId}
          onClose={() => setPeekUserId(null)}
          onOpenDm={openDm}
        />
      )}
    </div>
  );
}

function TierSection({
  tier,
  rows,
  mode,
  meId,
  onPeek,
}: {
  tier: RankTier;
  rows: LeaderboardRow[];
  mode: LeaderboardMode;
  meId: string | undefined;
  onPeek: (userId: string) => void;
}) {
  const t = useT();
  const accent = TIER_GLOW[tier];
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 4px 10px',
        }}
      >
        <RankMedal rank={tier} size={36} />
        <div>
          <div
            className="display"
            style={{
              fontSize: 16,
              letterSpacing: '0.08em',
              textShadow: `0 0 12px ${accent}`,
            }}
          >
            {TIER_LABEL[tier].toUpperCase()}
          </div>
          <div className="tag" style={{ color: 'var(--ink-dim)' }}>
            {t('leaderboard.top', { n: rows.length || 0 })}
          </div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div
          style={{
            padding: 14,
            border: '1px dashed var(--hairline)',
            borderRadius: 10,
            color: 'var(--ink-dim)',
            fontSize: 11,
            textAlign: 'center',
          }}
        >
          {t('leaderboard.tier_empty')}
        </div>
      ) : (
        rows.map((p, i) => (
          <LeaderRow
            key={p.id}
            place={i + 1}
            row={p}
            tab="mmr"
            mode={mode}
            mmrValue={mode === 'duo' ? p.mmrDuo : p.mmrSolo}
            isMe={p.id === meId}
            onPeek={() => onPeek(p.id)}
          />
        ))
      )}
    </div>
  );
}

function LeaderRow({
  place,
  row,
  tab,
  mode,
  mmrValue,
  isMe,
  onPeek,
}: {
  place: number;
  row: LeaderboardRow;
  tab: LeaderboardTab;
  mode: LeaderboardMode;
  mmrValue: number;
  isMe: boolean;
  onPeek: () => void;
}) {
  const t = useT();
  const rank = mode === 'duo' ? row.rankDuo : row.rank;
  const rankDivision = mode === 'duo' ? row.rankDivisionDuo : row.rankDivision;
  const placeBg =
    place === 1
      ? 'var(--c-yellow)'
      : place === 2
        ? '#d0d0d0'
        : place === 3
          ? '#cd7f32'
          : 'rgba(0,0,0,0.4)';
  const placeFg = place <= 3 ? '#1E1B4B' : 'var(--ink-muted)';

  return (
    <div
      onClick={onPeek}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        marginBottom: 6,
        cursor: 'pointer',
        background: isMe
          ? 'rgba(34,211,238,0.1)'
          : place <= 3
            ? 'rgba(244,199,36,0.06)'
            : 'rgba(0,0,0,0.3)',
        border: `1px solid ${
          isMe
            ? 'var(--c-cyan)'
            : place <= 3
              ? 'rgba(244,199,36,0.25)'
              : 'var(--hairline)'
        }`,
        borderRadius: 12,
        boxShadow: isMe ? '0 0 12px rgba(34,211,238,0.25)' : 'none',
      }}
    >
      <div
        style={{
          width: 28,
          height: 24,
          borderRadius: 6,
          flexShrink: 0,
          background: placeBg,
          color: placeFg,
          fontFamily: 'var(--f-display)',
          fontSize: 11,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {place}
      </div>
      <Avatar
        color={row.avatarColor}
        size={36}
        ring={false}
        photoUrl={row.avatarUrl}
        characterUrl={row.characterIdleUrl}
        frame={row.frame}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="display"
          style={{
            fontSize: 13,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.displayName}
          {isMe && (
            <span style={{ fontSize: 9, color: 'var(--c-cyan)', marginLeft: 6 }}>
              {t('leaderboard.you')}
            </span>
          )}
        </div>
        <div
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--ink-dim)',
            display: 'flex',
            gap: 8,
          }}
        >
          <span>
            {rank.slice(0, 3).toUpperCase()} {DIVISION_ROMAN[rankDivision] ?? 'IV'}
          </span>
          <span>·</span>
          <span>
            {row.matchesWon}/{row.matchesPlayed}W
          </span>
        </div>
      </div>
      <div style={{ textAlign: 'right', minWidth: 64 }}>
        {tab === 'time' ? (
          <>
            <div
              className="mono"
              style={{
                fontSize: 15,
                color: place === 1 ? 'var(--c-yellow)' : 'var(--c-cyan)',
                fontWeight: 700,
                textShadow:
                  place === 1 ? '0 0 8px rgba(244,199,36,0.5)' : 'none',
              }}
            >
              {formatTime(row.bestTimeMs)}
            </div>
            <div
              className="mono"
              style={{ fontSize: 9, color: 'var(--ink-dim)' }}
            >
              {mmrValue} MMR
            </div>
          </>
        ) : (
          <>
            <div
              className="mono"
              style={{
                fontSize: 15,
                color: 'var(--c-cyan)',
                fontWeight: 700,
              }}
            >
              {mmrValue}
            </div>
            <div className="mono" style={{ fontSize: 9, color: 'var(--ink-dim)' }}>
              {row.bestTimeMs ? formatTime(row.bestTimeMs) : '—'}
            </div>
          </>
        )}
      </div>
      <RankMedal rank={rank} size={28} />
    </div>
  );
}
